import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

// Fallback SEO metadata (your existing hardcoded data)
const fallbackSeoMetadata = {
  '/': {
    title: 'Professional Cleaning Services in Adelaide | Shiny Cleaning',
    description: 'Get professional house and commercial cleaning services in Adelaide. Shiny Cleaning provides eco-friendly, reliable solutions for all your cleaning needs. Book your free quote!'
  },
  '/adelaide-service': {
    title: 'Top-Rated Adelaide Cleaning Services & Shiny Cleaning',
    description: 'Get expert cleaning services in Adelaide with Shiny Cleaning. From house and office cleaning to specialized deep cleaning services. Book now for a free quote!'
  },
  '/melbourne-service': {
    title: 'Reliable Melbourne Cleaning Services | Shiny Cleaning',
    description: 'Looking for trusted cleaning services in Melbourne? Shiny Cleaning offers eco-friendly solutions for house, office cleaning, window, gutter cleaning, and carpet cleaning.'
  },
  '/service/house-cleaning/': {
    title: 'House Cleaning Service in Adelaide & Book Today!',
    description: 'Need house cleaning service in Adelaide? Trust Shiny Cleaning for eco-friendly, professional domestic cleaning. Book now for a spotless, fresh home today!'
  },
  '/service/once-off-house-cleaning/': {
    title: 'Once Off House Cleaning Service in Adelaide & Book Now!',
    description: 'Get reliable once-off house cleaning services in Adelaide with Shiny Cleaning. Offering expert deep cleaning solutions. Book now for your one-off house cleaner!'
  },
  '/service/regular-house-cleaning/': {
    title: 'Affordable Regular House Cleaning Services in Adelaide',
    description: 'Shiny Cleaning offers affordable regular house cleaning services in Adelaide. Book professional domestic cleaners. Top cleaning Adelaide!'
  },
  '/service/end-of-lease-cleaning/': {
    title: 'End of Lease Cleaning Service in Adelaide & Book Now!',
    description: 'Looking for end of lease cleaning service in Adelaide? Trust Shiny Cleaning for a spotless, stress-free bond clean. Book now for a smooth move-out. Fresh home!'
  },
  '/service/deep-cleaning/': {
    title: 'Expert Deep House Cleaning Service in Adelaide',
    description: 'Get the best deep house cleaning service in Adelaide with Shiny Cleaning. Our expert team guarantees spotless results. Book now for a fresh and clean space!'
  },
  '/service/commercial-cleaning/': {
    title: 'Top Commercial Cleaning Services in Adelaide | Call Now!',
    description: 'Shiny Cleaning provides top commercial cleaning services in Adelaide, including office, aged care, medical centre cleaning. Book your service now!'
  },
  '/service/office-cleaning/': {
    title: 'Get Professional Office Cleaning Services in Adelaide',
    description: 'Need office cleaning services in Adelaide? Shiny Cleaning offers eco-friendly and professional office cleaning to keep your workplace spotless. Book today!'
  },
  '/service/aged-care/': {
    title: 'Expert Aged Care Cleaning Service in Adelaide | Book Now!',
    description: 'Shiny Cleaning offers professional aged care cleaning services in Adelaide. Ensuring safe, hygienic environments for aged care facilities.'
  },
  '/service/school-cleaning-education-cleaning/': {
    title: 'Top School Cleaning Services in Adelaide | Shiny Cleaning',
    description: 'Shiny Cleaning offers experienced school cleaning services in Adelaide. Clean, safe & healthy environment for students & staff. Contact us for a free quote.'
  },
  '/service/medical-centre-cleaning/': {
    title: 'Trusted Medical Centre Cleaning Service in Adelaide',
    description: 'Shiny Cleaning provides professional medical centre cleaning services in Adelaide. Ensuring clean, safe for both your patients & staff. Book us now!'
  },
  '/service/professional-gutter-cleaning/': {
    title: 'Book Expert Gutter Cleaning Services in Adelaide Today!',
    description: 'Shiny Cleaning provides professional gutter cleaning services in Adelaide. Protect your home from water damage. Get your gutters cleaned today. Call us now!'
  },
  '/service/gutter-flushing/': {
    title: 'Reliable Gutter Flushing Services in Adelaide | Book Now!',
    description: 'Shiny Cleaning offers professional gutter flushing services in Adelaide to prevent water damage. Book a cleaning today for peace of mind!'
  },
  '/service/gutter-vacuum/': {
    title: 'Efficient Gutter Vacuum Services in Adelaide! Call Today',
    description: 'Keep your gutters clean and clog-free with our professional gutter vacuum services in Adelaide. Efficient, and affordable cleaning. Contact us today!'
  },
  '/service/roof-cleaning/': {
    title: 'Get Professional Roof Cleaning Service in Adelaide',
    description: 'Transform your roof with our professional roof cleaning services in Adelaide. Remove dirt, moss, algae for a spotless, long-lasting roof. Call us today!'
  },
  '/service/roof-pressure-washing/': {
    title: 'Affordable Roof Pressure Washing in Adelaide | Call Now!',
    description: 'Get roof pressure washing services in Adelaide with Shiny Cleaning. Professional roof cleaning for a spotless roof & fresh look! Schedule your service today!'
  },
  '/about-us/': {
    title: 'About Shiny Cleaning - Professional Cleaning Services Adelaide',
    description: 'Learn about Shiny Cleaning, Adelaide\'s trusted cleaning service provider. Professional, reliable, and eco-friendly cleaning solutions since 2010.'
  },
  '/contact-us': {
    title: 'Contact Shiny Cleaning Adelaide - Get Your Free Quote Today',
    description: 'Contact Shiny Cleaning for professional cleaning services in Adelaide. Get your free quote today! Call us or fill out our contact form.'
  },
  '/free-quote': {
    title: 'Get a Free Quote | Shiny Cleaning Services Adelaide',
    description: 'Looking for cleaning services in Adelaide? Get a free quote today. Our team will provide a customized solution for your cleaning needs. Reach out now!'
  }
};

// Default metadata for routes not configured
const defaultMetadata = {
  title: 'Shiny Cleaning - Professional Cleaning Services',
  description: 'Professional cleaning services in Adelaide and Melbourne. Eco-friendly, reliable solutions for all your cleaning needs.',
  keywords: 'cleaning services, Adelaide, Melbourne, professional cleaning, house cleaning, commercial cleaning'
};

const SEO = ({ 
  title, 
  description, 
  keywords,
  image = 'https://shinycleaner.com/og-image.jpg',
  url 
}) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const [dynamicMetadata, setDynamicMetadata] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Fetch meta tags from Laravel API
    const fetchMetaTags = async () => {
      try {
        setIsLoading(true);

        // Send the path as-is (API handles both with/without trailing slash)
        const response = await fetch(
          `https://api.shinycleaner.com/api/meta-tag?route=${encodeURIComponent(currentPath)}`
        );
        
        if (response.ok) {
          const data = await response.json();
          
          // Only set dynamic metadata if we got valid data
          if (data && (data.meta_title || data.meta_description)) {
            setDynamicMetadata({
              title: data.meta_title,
              description: data.meta_description,
              keywords: data.meta_keywords,
              ogTitle: data.og_title,
              ogDescription: data.og_description,
              ogImage: data.og_image ? `https://api.shinycleaner.com/storage/${data.og_image}` : null
            });
          }
        }
      } catch (error) {
        console.error('Error fetching meta tags from API:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMetaTags();
  }, [currentPath]);

  // Priority order for metadata:
  // 1. Props passed directly to the component
  // 2. Dynamic metadata from Laravel API
  // 3. Fallback hardcoded metadata
  // 4. Default metadata

  let pageTitle, pageDescription, pageKeywords, pageImage;

  if (title) {
    // Props have highest priority
    pageTitle = title;
    pageDescription = description;
    pageKeywords = keywords;
    pageImage = image;
  } else if (dynamicMetadata && dynamicMetadata.title) {
    // Use dynamic metadata from API
    pageTitle = dynamicMetadata.title;

    // If API description is missing, fall back to hardcoded description
    if (dynamicMetadata.description) {
      pageDescription = dynamicMetadata.description;
    } else {
      const fallbackData = fallbackSeoMetadata[currentPath] ||
                          fallbackSeoMetadata[currentPath.replace(/\/$/, '')] ||
                          fallbackSeoMetadata[currentPath + '/'] ||
                          {};
      pageDescription = fallbackData.description || defaultMetadata.description;
    }

    pageKeywords = dynamicMetadata.keywords;
    pageImage = dynamicMetadata.ogImage || image;
  } else {
    // Fall back to hardcoded or default metadata
    // Try exact path, then without slash, then with slash
    const fallbackData = fallbackSeoMetadata[currentPath] ||
                        fallbackSeoMetadata[currentPath.replace(/\/$/, '')] ||
                        fallbackSeoMetadata[currentPath + '/'] ||
                        {};

    pageTitle = fallbackData.title || defaultMetadata.title;
    pageDescription = fallbackData.description || defaultMetadata.description;
    pageKeywords = keywords || defaultMetadata.keywords;
    pageImage = image;
  }

  const canonicalUrl = url || `https://shinycleaner.com${currentPath}`;

  // For Open Graph, use specific OG data if available from API
  const ogTitle = dynamicMetadata?.ogTitle || pageTitle;
  const ogDescription = dynamicMetadata?.ogDescription || pageDescription;
  const ogImage = dynamicMetadata?.ogImage || pageImage;

  // Don't render until we've attempted to fetch dynamic metadata
  // This prevents flashing of default content
  if (isLoading) {
    // Still render with fallback data to avoid empty meta tags
    const fallbackData = fallbackSeoMetadata[currentPath] || {};
    const quickTitle = fallbackData.title || defaultMetadata.title;
    const quickDescription = fallbackData.description || defaultMetadata.description;
    
    return (
      <Helmet>
        <title>{quickTitle}</title>
        <meta name="description" content={quickDescription} />
      </Helmet>
    );
  }

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {pageKeywords && <meta name="keywords" content={pageKeywords} />}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="Shiny Cleaning" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Shiny Cleaning" />
      <meta property="og:locale" content="en_AU" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={ogDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content="@shinycleaning" />
      
      {/* Additional meta tags for better SEO */}
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="web" />
      <meta name="rating" content="general" />
      
      {/* Business-specific meta tags */}
      <meta name="geo.region" content="AU-SA" />
      <meta name="geo.placename" content="Adelaide" />
      <meta name="geo.position" content="-34.9285;138.6007" />
      
      {/* Schema.org structured data for local business */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Shiny Cleaning",
          "description": pageDescription,
          "url": canonicalUrl,
          "telephone": "+61406958557",
          "image": ogImage,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Para Hills",
            "addressLocality": "Adelaide",
            "addressRegion": "SA",
            "postalCode": "5096",
            "addressCountry": "AU"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": -34.9285,
            "longitude": 138.6007
          },
          "serviceArea": [
            {
              "@type": "City",
              "name": "Adelaide",
              "addressRegion": "SA",
              "addressCountry": "AU"
            },
            {
              "@type": "City", 
              "name": "Melbourne",
              "addressRegion": "VIC",
              "addressCountry": "AU"
            }
          ],
          "priceRange": "$$",
          "openingHours": "Mo-Su 07:00-19:00",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "reviewCount": "150"
          },
          "sameAs": [
            "https://www.facebook.com/shinycleaning",
            "https://www.google.com/maps/place/Shiny+Cleaning"
          ]
        })}
      </script>
    </Helmet>
  );
};

export default SEO;